import { useCallback } from "react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { prefetchNote } from "~/store/actions/actions.notes";
import { useGlobalPrivateChannelListener } from "../Hooks/useGlobalPrivateChannelListener";

export const NoteRealTime = ({ id, projectId, noteName, noteBody, notebookId }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const invalidateNavigation = useCallback(
    ({ project_id }) => {
      if (project_id === projectId) {
        queryClient.invalidateQueries(["note-navigation", project_id, id]);
      }
    },
    [id, projectId, queryClient]
  );

  const invalidateNoteAndNavigation = useCallback(
    ({ project_id, id: noteId }) => {
      if (project_id === projectId) {
        queryClient.invalidateQueries(["note-navigation", project_id, id]);
        noteId === id && dispatch(prefetchNote(project_id, id));
      }
    },
    [dispatch, id, projectId, queryClient]
  );

  const handleRestoreNotebook = useCallback(
    ({ project_id, id: notebook_id }) => {
      if (project_id === projectId && notebook_id === notebookId) {
        queryClient.invalidateQueries(["note-navigation", project_id, id]);
        dispatch(prefetchNote(projectId, id));
      }
    },
    [dispatch, id, notebookId, projectId, queryClient]
  );

  const handleMoveNotebookToTrash = useCallback(
    ({ project_id, id: notebook_id }) => {
      if (project_id === projectId && notebook_id === notebookId) {
        dispatch(prefetchNote(projectId, id));
        queryClient.invalidateQueries(["notebook", notebookId]);
      }
    },
    [dispatch, id, notebookId, projectId, queryClient]
  );

  const invalidateNote = useCallback(
    ({ project_id, id: noteId }) => {
      if (project_id === projectId && noteId === id) {
        dispatch(prefetchNote(projectId, id));
      }
    },
    [dispatch, id, projectId]
  );

  const invalidateForComments = useCallback(
    ({ parent_type, project_id }) => {
      if (parent_type === "Note" && projectId === project_id) {
        queryClient.invalidateQueries(["note-navigation", project_id, id]);
      }
    },
    [id, projectId, queryClient]
  );

  const invalidateVersions = useCallback(
    ({ name, body }) => {
      if (noteName !== name || noteBody !== body) {
        queryClient.invalidateQueries(["note-versions", projectId, id]);
      }
    },
    [id, projectId, queryClient, noteName, noteBody]
  );

  useGlobalPrivateChannelListener("NoteUpdated", invalidateVersions);
  useGlobalPrivateChannelListener("NoteUpdated", invalidateNavigation);
  useGlobalPrivateChannelListener("NoteCreated", invalidateNavigation);
  useGlobalPrivateChannelListener("NoteMovedToProject", invalidateNoteAndNavigation);
  useGlobalPrivateChannelListener("NoteRemovedFromProject", invalidateNavigation);
  useGlobalPrivateChannelListener("NoteMovedToTrash", invalidateNote);
  useGlobalPrivateChannelListener("NotebookMovedToTrash", handleMoveNotebookToTrash);
  useGlobalPrivateChannelListener("NotebookRestoredFromTrash", handleRestoreNotebook);
  useGlobalPrivateChannelListener("NoteRestoredFromTrash", invalidateNoteAndNavigation);
  useGlobalPrivateChannelListener("MoveNoteToNotebook", invalidateNavigation);
  useGlobalPrivateChannelListener("MoveNoteToProject", invalidateNavigation);

  useGlobalPrivateChannelListener("CommentCreated", invalidateForComments);
  useGlobalPrivateChannelListener("CommentMovedToTrash", invalidateForComments);
  useGlobalPrivateChannelListener("CommentRestoredFromTrash", invalidateForComments);

  return null;
};

NoteRealTime.propTypes = {
  projectId: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  notebookId: PropTypes.number,
  noteName: PropTypes.string,
  noteBody: PropTypes.string,
};

NoteRealTime.defaultProps = {
  notebookId: 0,
};
